/**
 * The §2 render decision table, as a pure function.
 *
 * `Brand_Design/myStack Dashboard - Engineering Handoff.html` §2 decides, per
 * recommended stack, whether it renders full, locked, or not at all — and §2's
 * last paragraph decides whether the *band* renders at all. Both live here
 * rather than inside the components because the failure this ticket is most
 * exposed to is silent: a card that renders in the wrong state looks fine.
 * A table you can assert row by row is the only defence.
 *
 * The `hidden` half is delegated to `services/systemStackPrefs.js`
 * (STACK-514), which owns the flag, its precondition table and the rule that
 * clears it. Nothing here re-derives "the profile caught up" — two answers to
 * that question is exactly the bug that module exists to prevent.
 */

import {
  SYSTEM_STACK_TYPES,
  isDietSupported,
  shouldHideLockedCard,
} from '../../services/systemStackPrefs';

/** What §2 resolves a single recommended stack to. */
export const RENDER_STATE = {
  /** Full recommended card — state 4a. */
  FULL: 'full',
  /** Locked card with unlock CTA — state 4b. */
  LOCKED: 'locked',
  /**
   * The profile has what the stack needs, but the stack has not been built
   * yet. §11 filed this as undesigned; the design-system brief specifies it
   * (the synthesis mark in the accent CTA) and STACK-515 ruled it in.
   */
  GENERATING: 'generating',
  /**
   * Lapsed member — state 4c. The card renders, its supplement detail does
   * not, and it carries one Reactivate CTA.
   *
   * **`resolveStackRenderState` never returns this**, and a test asserts so.
   * Everything else in this enum is a function of the profile and the `hidden`
   * flag; membership is a different axis entirely, and feeding subscription
   * state into §2's table would give one function two unrelated jobs and make
   * the table untestable row by row. The caller overlays it instead: the
   * dashboard reads `isLapsed()` and renders this state in place of whatever
   * the profile resolved to. Hiding still wins — a `NOT_RENDERED` stack is
   * never reached by the overlay, so a lapsed member does not get a card back
   * that they opted out of.
   */
  PAUSED: 'paused',
  /** Hidden by the user's own opt-out — renders nothing at all. */
  NOT_RENDERED: 'not-rendered',
};

/** The order §2 lists them in; the band never re-sorts (§5, Sort). */
export const RECOMMENDED_STACK_ORDER = [
  SYSTEM_STACK_TYPES.MEDICATION,
  SYSTEM_STACK_TYPES.DIET,
];

/**
 * Whether the profile carries what this stack is built from.
 *
 * Deliberately expressed through `systemStackPrefs`' exported predicate for
 * diet rather than a local `.length` / `includes` check, so the supported-diet
 * list has exactly one definition in the frontend.
 */
function profileHasInput(stackType, profile) {
  if (stackType === SYSTEM_STACK_TYPES.MEDICATION) {
    return Array.isArray(profile?.medications) && profile.medications.length > 0;
  }
  return isDietSupported(profile?.diet);
}

/**
 * Resolve one stack's row of §2.
 *
 * @param {Object}  args
 * @param {string}  args.stackType - a `SYSTEM_STACK_TYPES` value
 * @param {Object}  args.userData  - the loaded `users/{uid}` document
 * @param {Object}  args.profile   - `{ medications, diet }`
 * @param {Object=} args.stack     - the built system stack, if one exists
 * @param {boolean=} args.isBuilding - a generate/poll cycle is in flight
 * @returns {string} a `RENDER_STATE` value
 */
export function resolveStackRenderState({
  stackType,
  userData,
  profile,
  stack,
  isBuilding = false,
}) {
  // Throws on an unknown stack type, via systemStackPrefs' precondition table.
  // A typo must be loud here rather than silently falling through to LOCKED.
  if (shouldHideLockedCard(userData, stackType, profile)) {
    return RENDER_STATE.NOT_RENDERED;
  }

  if (!profileHasInput(stackType, profile)) {
    return RENDER_STATE.LOCKED;
  }

  // The profile is ready. Either the stack is here, or it is being built —
  // there is no third case, and "ready but empty and idle" is deliberately
  // shown as generating rather than as a locked card: a locked card would ask
  // the user for data they have already given.
  if (stack && Array.isArray(stack.supplements) && stack.supplements.length > 0) {
    return isBuilding ? RENDER_STATE.GENERATING : RENDER_STATE.FULL;
  }

  return RENDER_STATE.GENERATING;
}

/**
 * Resolve the whole band: one entry per stack, in §2's order, plus whether the
 * band itself renders.
 *
 * §2: "When both stacks resolve to 'not rendered', omit the whole Recommended
 * band — label, freshness line and all." `bandRenders` is false in exactly that
 * case, and the caller swaps the greeting subhead to the §9 recovery string.
 * Note this is *both*, not *any*: one hidden stack still leaves a band.
 *
 * @returns {{bandRenders: boolean, stacks: Array<{stackType, state, stack}>}}
 */
export function resolveBand({ userData, profile, stacksByType = {}, buildingByType = {} }) {
  const stacks = RECOMMENDED_STACK_ORDER.map((stackType) => ({
    stackType,
    stack: stacksByType[stackType] || null,
    state: resolveStackRenderState({
      stackType,
      userData,
      profile,
      stack: stacksByType[stackType],
      isBuilding: Boolean(buildingByType[stackType]),
    }),
  }));

  return {
    bandRenders: stacks.some((entry) => entry.state !== RENDER_STATE.NOT_RENDERED),
    stacks,
  };
}
